import { useEffect, useRef, useState, useCallback } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import Tesseract from 'tesseract.js';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Camera, CameraOff, X, Type, Loader2, RotateCw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface VinScannerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onVinDetected: (vin: string) => void;
}

/**
 * Normalizes raw scan result and validates as a VIN.
 * VINs are exactly 17 alphanumeric characters, excluding I, O, Q.
 */
function normalizeAndValidateVin(raw: string): string | null {
  const vin = raw.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
  const isValid = /^[A-HJ-NPR-Z0-9]{17}$/.test(vin);
  return isValid ? vin : null;
}

/**
 * Extract all valid VINs from OCR text.
 * Includes noise filtering for OCR artifacts.
 */
function extractVinsFromText(text: string): string[] {
  // Normalize: uppercase, keep only valid VIN chars + whitespace
  const normalized = text.toUpperCase().replace(/[^A-HJ-NPR-Z0-9\s]/g, '');
  
  // Find all 17-character VIN matches
  const matches = normalized.match(/[A-HJ-NPR-Z0-9]{17}/g) || [];
  
  // Filter out OCR noise
  const validVins = matches.filter(vin => {
    // Reject all-digit VINs (real VINs must have letters)
    if (/^\d{17}$/.test(vin)) return false;
    // Reject 17 repeated characters (e.g., "AAAAAAAAAAAAAAAAA")
    if (/^(.)\1{16}$/.test(vin)) return false;
    return true;
  });
  
  return [...new Set(validVins)];
}

export function VinScannerDialog({ open, onOpenChange, onVinDetected }: VinScannerDialogProps) {
  const [error, setError] = useState<string | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [isOcrProcessing, setIsOcrProcessing] = useState(false);
  const [ocrMatches, setOcrMatches] = useState<string[]>([]);
  const [showVinPicker, setShowVinPicker] = useState(false);
  const [isPortrait, setIsPortrait] = useState(
    typeof window !== 'undefined' && window.matchMedia('(orientation: portrait)').matches
  );
  const [manualVertical, setManualVertical] = useState<boolean | null>(null);
  
  // Compute effective orientation: manual override takes precedence
  const useVerticalBar = manualVertical !== null ? manualVertical : isPortrait;
  
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const hasDetectedRef = useRef(false);
  const scannerContainerId = 'vin-scanner-container';
  
  const { toast } = useToast();

  // Detect orientation changes
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mediaQuery = window.matchMedia('(orientation: portrait)');
    const handleChange = (e: MediaQueryListEvent) => setIsPortrait(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const stopScanner = useCallback(async () => {
    if (scannerRef.current) {
      try {
        const state = scannerRef.current.getState();
        if (state === 2) { // Html5QrcodeScannerState.SCANNING
          await scannerRef.current.stop();
        }
      } catch (e) {
        // Scanner may already be stopped
      }
      try {
        scannerRef.current.clear();
      } catch (e) {
        // Container may already be cleared
      }
      scannerRef.current = null;
    }
    setIsScanning(false);
  }, []);

  const startScanner = useCallback(async () => {
    setError(null);
    hasDetectedRef.current = false;

    await new Promise(resolve => setTimeout(resolve, 100));

    const container = document.getElementById(scannerContainerId);
    if (!container) {
      setError('Scanner container not found');
      return;
    }

    try {
      const scanner = new Html5Qrcode(scannerContainerId);
      scannerRef.current = scanner;

      // Use computed orientation (respects manual override)
      const qrboxConfig = useVerticalBar 
        ? { width: 100, height: 280 }   // Vertical bar
        : { width: 280, height: 100 };  // Horizontal bar
      const aspectRatioConfig = useVerticalBar ? 0.5 : 2.0;

      await scanner.start(
        { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: qrboxConfig,
          aspectRatio: aspectRatioConfig,
        },
        (decodedText) => {
          if (hasDetectedRef.current) return;

          const validVin = normalizeAndValidateVin(decodedText);
          if (validVin) {
            hasDetectedRef.current = true;
            stopScanner();
            onVinDetected(validVin);
          }
        },
        () => {
          // Ignore failed scans
        }
      );

      setIsScanning(true);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      
      if (errorMessage.includes('NotAllowedError') || errorMessage.includes('Permission')) {
        setError('Camera permission denied. Please allow camera access and try again.');
      } else if (errorMessage.includes('NotFoundError') || errorMessage.includes('no camera')) {
        setError('No camera found on this device.');
      } else if (errorMessage.includes('NotReadableError')) {
        setError('Camera is in use by another application.');
      } else {
        setError(`Camera error: ${errorMessage}`);
      }
      
      setIsScanning(false);
    }
  }, [onVinDetected, stopScanner, useVerticalBar]);

  const captureFrame = useCallback(async (): Promise<string> => {
    const video = document.querySelector(`#${scannerContainerId} video`) as HTMLVideoElement;
    if (!video) throw new Error('Video element not found');
    
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas context not available');
    
    ctx.drawImage(video, 0, 0);
    return canvas.toDataURL('image/png');
  }, []);

  const handleOcrCapture = useCallback(async () => {
    if (!scannerRef.current || isOcrProcessing) return;
    
    setIsOcrProcessing(true);
    setOcrMatches([]);
    setShowVinPicker(false);
    
    try {
      // Pause barcode scanning (keeps video feed visible)
      scannerRef.current.pause(false);
      
      const imageDataUrl = await captureFrame();
      
      const { data: { text } } = await Tesseract.recognize(imageDataUrl, 'eng');
      
      const vins = extractVinsFromText(text);
      
      if (vins.length === 0) {
        toast({
          title: "No VIN detected",
          description: "Try better lighting, reduce glare, and hold camera steady.",
          variant: "destructive",
        });
        scannerRef.current.resume();
      } else if (vins.length === 1) {
        hasDetectedRef.current = true;
        stopScanner();
        onVinDetected(vins[0]);
      } else {
        setOcrMatches(vins);
        setShowVinPicker(true);
      }
    } catch (error) {
      console.error('OCR error:', error);
      toast({
        title: "OCR failed",
        description: "Could not process image. Try again.",
        variant: "destructive",
      });
      scannerRef.current?.resume();
    } finally {
      setIsOcrProcessing(false);
    }
  }, [isOcrProcessing, captureFrame, stopScanner, onVinDetected, toast]);

  const handleVinSelect = useCallback((vin: string) => {
    hasDetectedRef.current = true;
    setShowVinPicker(false);
    setOcrMatches([]);
    stopScanner();
    onVinDetected(vin);
  }, [stopScanner, onVinDetected]);

  const handleTryAgain = useCallback(() => {
    setShowVinPicker(false);
    setOcrMatches([]);
    scannerRef.current?.resume();
  }, []);

  // Restart scanner when dialog opens, orientation changes, or manual toggle
  useEffect(() => {
    if (open) {
      // Stop and restart to apply new orientation config
      stopScanner().then(() => startScanner());
    } else {
      stopScanner();
      hasDetectedRef.current = false;
      setOcrMatches([]);
      setShowVinPicker(false);
      setManualVertical(null); // Reset manual override when dialog closes
    }

    return () => {
      stopScanner();
    };
  }, [open, useVerticalBar, startScanner, stopScanner]);

  const handleClose = () => {
    stopScanner();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="h-5 w-5" />
            Scan VIN Barcode
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Scanner viewport */}
          <div 
            id={scannerContainerId}
            className="w-full min-h-[200px] bg-muted rounded-lg overflow-hidden"
          />

          {/* Error state */}
          {error && (
            <div className="flex items-start gap-3 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
              <CameraOff className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="text-sm text-destructive font-medium">Camera Unavailable</p>
                <p className="text-sm text-muted-foreground">{error}</p>
                <p className="text-sm text-muted-foreground">You can enter the VIN manually instead.</p>
              </div>
            </div>
          )}

          {/* Scanning indicator with tips */}
          {isScanning && !error && !showVinPicker && (
            <div className="space-y-2 text-center">
              <p className="text-sm text-muted-foreground">
                Point your camera at the VIN barcode (usually on driver's door jamb or windshield)
              </p>
              <p className="text-xs text-muted-foreground">
                <strong>Tips:</strong> Avoid glare and shadows. Hold steady. Ensure good lighting.
              </p>
            </div>
          )}

          {/* Flip Scan Bar Orientation Button */}
          {isScanning && !error && !showVinPicker && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setManualVertical(prev => prev === null ? !isPortrait : !prev)}
              className="w-full"
            >
              <RotateCw className="h-4 w-4 mr-2" />
              Flip Scan Bar ({useVerticalBar ? 'Vertical' : 'Horizontal'} → {useVerticalBar ? 'Horizontal' : 'Vertical'})
            </Button>
          )}

          {/* VIN Picker for multiple OCR matches */}
          {showVinPicker && ocrMatches.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium">Multiple VINs detected. Select one:</p>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {ocrMatches.map((vin, index) => (
                  <Button
                    key={vin}
                    type="button"
                    variant="outline"
                    onClick={() => handleVinSelect(vin)}
                    className="w-full justify-start font-mono text-xs"
                  >
                    {index + 1}. {vin}
                  </Button>
                ))}
              </div>
              <Button
                type="button"
                variant="ghost"
                onClick={handleTryAgain}
                className="w-full"
              >
                Try again
              </Button>
            </div>
          )}

          {/* OCR Fallback Button */}
          {isScanning && !error && !showVinPicker && (
            <Button
              type="button"
              variant="secondary"
              onClick={handleOcrCapture}
              disabled={isOcrProcessing}
              className="w-full"
            >
              {isOcrProcessing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Reading VIN text...
                </>
              ) : (
                <>
                  <Type className="h-4 w-4 mr-2" />
                  Scan VIN Text (OCR)
                </>
              )}
            </Button>
          )}

          {/* HTTPS notice */}
          {typeof window !== 'undefined' && !window.isSecureContext && (
            <p className="text-xs text-amber-600 text-center">
              Camera requires HTTPS. Please access this site via a secure connection.
            </p>
          )}

          {/* Close button */}
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            className="w-full"
          >
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
